// src/components/VoiceNarrationScreen.js
import React, { useState, useRef, useEffect } from 'react';
import ChevronLeft from 'lucide-react/dist/esm/icons/chevron-left';
import Play from 'lucide-react/dist/esm/icons/play';
import Pause from 'lucide-react/dist/esm/icons/pause';
import Volume2 from 'lucide-react/dist/esm/icons/volume-2';
import Download from 'lucide-react/dist/esm/icons/download';
import RefreshCw from 'lucide-react/dist/esm/icons/refresh-cw';
import CreditCard from 'lucide-react/dist/esm/icons/credit-card';
import Clock from 'lucide-react/dist/esm/icons/clock';
import Sliders from 'lucide-react/dist/esm/icons/sliders';

const VoiceNarrationScreen = ({ onBack }) => {
  const [text, setText] = useState('');
  const [voice, setVoice] = useState('21m00Tcm4TlvDq8ikWAM'); // Rachel voice ID
  const [stability, setStability] = useState(0.5);
  const [similarityBoost, setSimilarityBoost] = useState(0.75);
  const [style, setStyle] = useState(0);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedAudio, setGeneratedAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  // ElevenLabsの音声リスト
  const voices = [
    { id: '21m00Tcm4TlvDq8ikWAM', name: 'Rachel', description: '明るく自然な女性の声' },
    { id: 'AZnzlk1XvdvUeBnXmlld', name: 'Domi', description: '透明感のある女性の声' },
    { id: 'EXAVITQu4vr4xnSDxMaL', name: 'Bella', description: '優しく柔らかい女性の声' },
    { id: 'ErXwobaYiN019PkySvjV', name: 'Antoni', description: '落ち着いた男性の声' },
    { id: 'MF3mGyEYCl7XYWbV9V6O', name: 'Elli', description: '感情豊かな女性の声' },
    { id: 'TxGEqnHWrfWFTfGW9XjX', name: 'Josh', description: '元気な男性の声' },
    { id: 'VR6AewLTigWG4xSOukaG', name: 'Arnold', description: '力強い男性の声' },
    { id: 'pNInz6obpgDQGcFmaJgB', name: 'Adam', description: '自然で明瞭な男性の声' }
  ];

  // 音声のスタイル
  const styleOptions = [
    { value: 0, name: '標準' },
    { value: 0.25, name: '楽しい' },
    { value: 0.5, name: '悲しい' },
    { value: 0.75, name: '興奮した' },
    { value: 1, name: '怒った' }
  ];

  // オーディオの再生/停止を制御
  const togglePlay = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  // オーディオの再生状態を監視
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.onplay = () => setIsPlaying(true);
      audioRef.current.onpause = () => setIsPlaying(false);
      audioRef.current.onended = () => setIsPlaying(false);
    }
    
    return () => {
      if (audioRef.current) {
        audioRef.current.onplay = null;
        audioRef.current.onpause = null;
        audioRef.current.onended = null;
      }
    };
  }, [generatedAudio]);

  // 音声生成APIを呼び出す
  const generateVoice = async () => {
    if (!text) return;
    
    setIsGenerating(true);
    setError(null);
    
    try {
      // Cloudflare Workersを使ってAPIリクエスト
      const response = await fetch('https://elevenlabs-worker.kgu-ishizaka.workers.dev/generate-voice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          text,
          voice_id: voice,
          stability,
          similarity_boost: similarityBoost,
          style
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '音声生成に失敗しました');
      }
      
      // 音声データをBlobとして取得
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      setGeneratedAudio(audioUrl);
    } catch (err) {
      console.error('音声生成エラー:', err);
      setError(err.message || '音声の生成中にエラーが発生しました。もう一度お試しください。');
    } finally {
      setIsGenerating(false);
    }
  };

  // 音声のダウンロード
  const handleDownload = () => {
    if (!generatedAudio) return;
    
    const a = document.createElement('a');
    a.href = generatedAudio;
    a.download = `saku-ai-voice-${new Date().getTime()}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // 必要クレジットの計算
  const calculateCredits = () => {
    // テキストの長さによってクレジットを計算
    const charCount = text.length;
    return Math.max(1, Math.ceil(charCount / 100)); // 100文字ごとに1クレジット、最低1クレジット
  };

  return (
    <div className="bg-gray-100">
      <div className="bg-white px-4 py-3 flex items-center shadow">
        <button 
          onClick={onBack}
          className="text-blue-500 mr-3"
        >
          <ChevronLeft size={24} />
        </button>
        <h2 className="text-lg font-bold">音声ナレーション</h2>
      </div>
      
      <div className="p-4">
        {/* テキスト入力エリア */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-700">ナレーションテキスト</label>
          <textarea
            className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
            placeholder="読み上げたいテキストを入力してください（例：こんにちは、これはAI音声アシスタントです。）"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
          />
          <div className="mt-1 text-right text-xs text-gray-500">
            {text.length} 文字
          </div>
        </div>

        {/* 音声選択 */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-700">音声</label>
          <select
            className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            value={voice}
            onChange={(e) => setVoice(e.target.value)}
          >
            {voices.map(v => (
              <option key={v.id} value={v.id}>
                {v.name} - {v.description}
              </option>
            ))}
          </select>
        </div>

        {/* 詳細設定トグル */}
        <button 
          className="w-full flex items-center justify-center py-2 text-blue-500 text-sm font-medium mb-4"
          onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
        >
          詳細設定
          <Sliders size={14} className="ml-1" />
        </button>

        {/* 詳細設定パネル */}
        {isAdvancedOpen && (
          <div className="mb-4 bg-white rounded-lg shadow-md p-4">
            <div className="mb-4">
              <label className="flex justify-between text-sm font-medium mb-1 text-gray-700">
                <span>安定性 ({stability})</span>
                <span className="text-gray-500 text-xs">低い値: 多様性、高い値: 安定性</span>
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={stability}
                onChange={(e) => setStability(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            <div className="mb-4">
              <label className="flex justify-between text-sm font-medium mb-1 text-gray-700">
                <span>音声の類似度 ({similarityBoost})</span>
                <span className="text-gray-500 text-xs">低い値: 創造性、高い値: 忠実性</span>
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={similarityBoost}
                onChange={(e) => setSimilarityBoost(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">スタイル</label>
              <div className="flex flex-wrap gap-2">
                {styleOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      style === option.value 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    onClick={() => setStyle(option.value)}
                  >
                    {option.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* 生成ボタンと使用クレジット */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center text-gray-500 text-sm">
              <CreditCard size={16} className="mr-1" />
              <span>使用クレジット: {calculateCredits()}</span>
            </div>
            <div className="flex items-center text-gray-500 text-sm">
              <Clock size={16} className="mr-1" />
              <span>推定時間: {Math.ceil(Math.max(1, text.length) / 20)}秒</span>
            </div>
          </div>
          
          <button
            className={`w-full py-3 rounded-lg font-medium text-white ${
              isGenerating || !text 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
            }`}
            onClick={generateVoice}
            disabled={isGenerating || !text}
          >
            {isGenerating ? '生成中...' : '音声を生成'}
          </button>
        </div>

        {/* エラーメッセージ */}
        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* 生成された音声 */}
        {generatedAudio && (
          <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-medium">生成された音声</h3>
              <div className="flex space-x-2">
                <button 
                  className="text-blue-500 p-1 hover:bg-blue-50 rounded-md transition-colors"
                  onClick={handleDownload}
                >
                  <Download size={18} />
                </button>
                <button 
                  className="text-blue-500 p-1 hover:bg-blue-50 rounded-md transition-colors"
                  onClick={generateVoice}
                  disabled={isGenerating}
                >
                  <RefreshCw size={18} className={isGenerating ? "animate-spin" : ""} />
                </button>
              </div>
            </div>
            
            <div className="flex items-center bg-gray-100 rounded-lg p-2">
              <button
                className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white mr-3"
                onClick={togglePlay}
              >
                {isPlaying ? <Pause size={20} /> : <Play size={20} />}
              </button>
              
              <div className="flex-1">
                <div className="relative w-full h-2 bg-gray-300 rounded">
                  <div className="absolute h-full bg-blue-500 rounded" style={{ width: '0%', transition: 'width 0.1s linear' }}></div>
                </div>
              </div>
              
              <Volume2 size={18} className="ml-3 text-gray-500" />
              
              {/* 非表示のオーディオ要素 */}
              <audio ref={audioRef} src={generatedAudio} preload="auto" />
            </div>
          </div>
        )}
        
        {/* 音声サンプル */}
        <div className="bg-white rounded-lg shadow-lg p-4">
          <h3 className="font-medium mb-3">音声サンプル</h3>
          <div className="grid grid-cols-2 gap-2">
            {voices.slice(0, 4).map((v) => (
              <button 
                key={v.id} 
                className={`p-3 rounded-lg border text-left transition-colors ${
                  voice === v.id 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
                onClick={() => setVoice(v.id)}
              >
                <div className="font-medium text-sm">{v.name}</div>
                <div className="text-xs text-gray-500 mt-1">{v.description}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceNarrationScreen;